// Phase 6 — station × config heatmap.
//
// Cells are coloured by mean dR via a 7-stop HSL gradient. The
// **direction is monotonic**: 0 mm = the most saturated dark
// green, 100+ mm = the deepest red, with a smooth slide through
// yellow at the 15-mm boundary. Phase 6.1 (palette inversion)
// flipped the green zone so that the best result is the most
// visually prominent — see ``drColor`` for the breakpoints.
//
// Empty cells show "—" on a neutral surface. Hover surfaces a
// tooltip with the exact value + sample count via the native
// ``title`` attribute (works inside Telegram WebApp too).

import { Fragment, memo, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import type {
  AnalyticsHeatmap,
  AnalyticsHeatmapCell,
  FailedCategory,
} from '../../api/types';
import { formatConfig } from './formatConfig';


/** Phase 6.3 → Phase R — short tooltip label for the v2
 *  FailedCategory, retitled under the Phase R taxonomy. Kept
 *  consistent with BatchSummaryFeedCard so the same row reads
 *  identically across surfaces. */
const FAILED_CATEGORY_SHORT_LABEL: Record<FailedCategory, string> = {
  rinex_real_missing: 'не применимо',
  rinex_parse_error: 'прочее',
  rinex_not_available: 'файл недоступен',
  compute_failed_ambiguity: 'не вычислено',
  service_compute_failed: 'не вычислено',
  other: 'прочее',
};

/** Phase 6.1 — Enter / Space activate the clickable cells/headers
 *  for keyboard users. Standard a11y idiom for ``role="button"``
 *  elements (the spec's B.4.8 acceptance criterion). */
function _onKeyboardActivate(
  handler: () => void,
): (e: KeyboardEvent<HTMLElement>) => void {
  return (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handler();
    }
  };
}

/** Phase 6.1 — 7-stop HSL gradient. Each tuple is (dr_mm, h, s, l).
 *
 *  Monotonic semantics, validated by the visual-acceptance fixtures
 *  in the spec:
 *    * 0 mm   → saturated dark green   — best
 *    * 5 mm   → mid green
 *    * 10 mm  → light green
 *    * 15 mm  → yellow-chartreuse      — bottom of "needs attention"
 *    * 30 mm  → yellow-orange
 *    * 50 mm  → orange-red
 *    * 100 mm → deep saturated red     — worst
 *
 *  Values above 100 mm clamp on the last stop; ``null`` falls
 *  through to the neutral surface token. */
type _DrStop = readonly [number, number, number, number];

const _DR_STOPS: readonly _DrStop[] = [
  [0, 140, 60, 38],
  [5, 135, 55, 50],
  [10, 120, 45, 70],
  [15, 75, 70, 60],
  [30, 35, 85, 55],
  [50, 10, 75, 55],
  [100, 0, 75, 45],
];

// Non-empty by construction — pre-extracted so the indexed accesses
// below don't fight TypeScript's noUncheckedIndexedAccess.
const _FIRST_STOP: _DrStop = _DR_STOPS[0] as _DrStop;
const _LAST_STOP: _DrStop = _DR_STOPS[_DR_STOPS.length - 1] as _DrStop;

/** Linearly interpolate ``t`` ∈ [0, 1] between ``a`` and ``b``. */
function _lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** HSL-based color scale. Exposed for the legend + tests. */
export function drColor(drMm: number | null): string {
  if (drMm === null) return 'var(--surface-2)';
  // Below-zero defensive clamp — shouldn't happen for a magnitude,
  // but keeps the interpolation total.
  if (drMm <= _FIRST_STOP[0]) {
    const [, h, s, l] = _FIRST_STOP;
    return `hsl(${h}, ${s}%, ${l}%)`;
  }
  // Above the last breakpoint — clamp on the final colour so
  // 547-mm outliers don't keep getting darker indefinitely.
  if (drMm >= _LAST_STOP[0]) {
    return `hsl(${_LAST_STOP[1]}, ${_LAST_STOP[2]}%, ${_LAST_STOP[3]}%)`;
  }
  // Find the bracketing pair and interpolate each component.
  for (let i = 1; i < _DR_STOPS.length; i++) {
    const hi = _DR_STOPS[i] as _DrStop;
    if (drMm <= hi[0]) {
      const lo = _DR_STOPS[i - 1] as _DrStop;
      const t = (drMm - lo[0]) / (hi[0] - lo[0]);
      const h = Math.round(_lerp(lo[1], hi[1], t));
      const s = Math.round(_lerp(lo[2], hi[2], t));
      const l = Math.round(_lerp(lo[3], hi[3], t));
      return `hsl(${h}, ${s}%, ${l}%)`;
    }
  }
  // Unreachable (covered by the clamps above), but TypeScript needs
  // a terminal return for the function's return type.
  return `hsl(${_LAST_STOP[1]}, ${_LAST_STOP[2]}%, ${_LAST_STOP[3]}%)`;
}

/** Phase 6.1 — derive the cell's lightness via the same 7-stop
 *  interpolation and pick a contrast-safe text colour. Light text
 *  for cells whose computed L drops below 55% (saturated dark green
 *  at 0-5 mm + every yellow/orange/red cell from ~25 mm onwards). */
function drTextColor(drMm: number | null): string {
  if (drMm === null) return 'var(--ink-3)';
  if (drMm <= _FIRST_STOP[0]) return '#fff';
  if (drMm >= _LAST_STOP[0]) return '#fff';
  for (let i = 1; i < _DR_STOPS.length; i++) {
    const hi = _DR_STOPS[i] as _DrStop;
    if (drMm <= hi[0]) {
      const lo = _DR_STOPS[i - 1] as _DrStop;
      const t = (drMm - lo[0]) / (hi[0] - lo[0]);
      const l = _lerp(lo[3], hi[3], t);
      return l < 55 ? '#fff' : 'var(--ink)';
    }
  }
  return 'var(--ink)';
}

/** Phase 6.6 E — sort modes for the heatmap station rows.
 *
 *  * `name`     — alphabetical by station code (default; matches
 *                 pre-6.6 behaviour).
 *  * `coverage` — DESC by count of numeric cells; operator sees
 *                 the stations with the most-complete data first.
 *  * `accuracy` — ASC by median |dR| across the row's numeric
 *                 cells; best-performing stations float to the top.
 *
 *  Sort is applied to the «filled» partition only (rows with at
 *  least one numeric cell). Empty rows always go into the
 *  collapsible «СТАНЦИИ БЕЗ РЕЗУЛЬТАТОВ» section at the tail —
 *  see `_partitionByCoverage` below. */
type SortMode = 'name' | 'coverage' | 'accuracy';

const _SORT_LABELS: Record<SortMode, string> = {
  name: 'по имени',
  coverage: 'по покрытию конфигов',
  accuracy: 'по точности',
};

/** Median over a non-empty list of floats. Stable enough for our
 *  cohort sizes — sorts in-place on a copy then picks the midpoint
 *  (or averages the two midpoints for even-length lists). Returns
 *  `null` if the list is empty so the sort comparator can route
 *  empty rows to the tail without an Infinity sentinel. */
function _median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid]!;
  return (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/** Numeric-cell count for one station row.
 *
 *  Phase G.2 — skip-cells (✗ / ○) ALSO count as filled. They
 *  represent DONE jobs that produced diagnostic signal — operator
 *  wants the row in the main table, not behind a collapsible.
 *  FAILED-only rows still partition to «СТАНЦИИ БЕЗ РЕЗУЛЬТАТОВ»
 *  (matches pre-G.2 behaviour — a station whose every cell is a
 *  legacy «—» wasn't expected to appear in the main view either). */
function _filledCount(
  cells: AnalyticsHeatmapCell[],
  station: string,
): number {
  return cells.filter(
    (c) =>
      c.station === station &&
      (c.avg_dr_mm !== null ||
        (c.skip_compute_failed_count ?? 0) > 0 ||
        (c.skip_no_reference_count ?? 0) > 0 ||
        // P1 · 2B — a technical/indeterminate cell is still a signal: the
        // station stays in the visible «filled» partition, not the empty tail.
        (c.skip_operational_failure_count ?? 0) > 0 ||
        (c.skip_unknown_count ?? 0) > 0 ||
        (c.skip_invariant_violation_count ?? 0) > 0),
  ).length;
}

/** Row median over the numeric cells — drives the «по точности»
 *  sort. Empty rows return `null` and sort to the tail. */
function _rowMedian(
  cells: AnalyticsHeatmapCell[],
  station: string,
): number | null {
  const values = cells
    .filter((c) => c.station === station && c.avg_dr_mm !== null)
    .map((c) => c.avg_dr_mm as number);
  return _median(values);
}

/** Phase 6.6 F — split stations into «filled» (at least one
 *  numeric cell) and «empty» (every cell is null) partitions.
 *  Empty rows skip the sort and land in the collapsible tail
 *  section regardless of sort mode (per spec §F.3). */
function _partitionByCoverage(
  data: AnalyticsHeatmap,
): { filled: string[]; empty: string[] } {
  const filled: string[] = [];
  const empty: string[] = [];
  for (const station of data.stations) {
    if (_filledCount(data.cells, station) > 0) filled.push(station);
    else empty.push(station);
  }
  return { filled, empty };
}

function _sortStations(
  stations: string[],
  cells: AnalyticsHeatmapCell[],
  mode: SortMode,
): string[] {
  if (mode === 'name') {
    return [...stations].sort((a, b) => a.localeCompare(b));
  }
  if (mode === 'coverage') {
    return [...stations].sort((a, b) => {
      const aFilled = _filledCount(cells, a);
      const bFilled = _filledCount(cells, b);
      // DESC — most coverage first; stable tiebreak by name ASC.
      if (aFilled !== bFilled) return bFilled - aFilled;
      return a.localeCompare(b);
    });
  }
  // accuracy
  return [...stations].sort((a, b) => {
    const aMed = _rowMedian(cells, a);
    const bMed = _rowMedian(cells, b);
    // Null-medians (shouldn't appear after partitioning, but
    // defensive) sort to the tail.
    if (aMed === null && bMed === null) return a.localeCompare(b);
    if (aMed === null) return 1;
    if (bMed === null) return -1;
    if (aMed !== bMed) return aMed - bMed; // ASC — best first
    return a.localeCompare(b);
  });
}

function HeatmapStationConfigImpl({ data }: { data: AnalyticsHeatmap }) {
  const navigate = useNavigate();
  // Phase 6.6 E — sort mode is in-memory (per spec §E.2 — no
  // localStorage). Defaults to «по имени», which matches the
  // pre-6.6 alphabetical ordering of the backend response.
  const [sortMode, setSortMode] = useState<SortMode>('name');
  // Phase 6.6 F — collapsed-by-default section for empty rows.
  // Auto-expands when EVERY station is empty (spec open
  // question 6: «нечего скрывать»). Operator can still click
  // the toggle to fold it manually in that edge case.
  const [emptyExpanded, setEmptyExpanded] = useState(false);

  // Phase 6.6 F partition — compute once per data ref. Memoised
  // so the sort recompute below doesn't trigger this twice.
  const { filled, empty } = useMemo(
    () => _partitionByCoverage(data),
    [data],
  );
  const sortedFilled = useMemo(
    () => _sortStations(filled, data.cells, sortMode),
    [filled, data.cells, sortMode],
  );
  // C1.1 — O(1) cell lookup for renderRow (was `_cell` = cells.find per cell,
  // S×C per render, outside useMemo). First-match kept, so identical values.
  const cellByKey = useMemo(() => {
    const m = new Map<string, AnalyticsHeatmapCell>();
    for (const c of data.cells) {
      const k = `${c.station}|${c.config}`;
      if (!m.has(k)) m.set(k, c);
    }
    return m;
  }, [data.cells]);
  // If every station is empty, force the section open so the
  // operator isn't staring at an «N stations, click to reveal»
  // toggle with nothing else on screen.
  const effectiveEmptyExpanded = filled.length === 0 || emptyExpanded;

  if (data.stations.length === 0 || data.configs.length === 0) {
    return (
      <div className="heatmap-section">
        <div className="section-title">точность по станциям и конфигам</div>
        <div className="analytics-empty">нет данных по выбранным фильтрам</div>
      </div>
    );
  }

  // Phase 6.1 + 6.3 + G.2 — five click flavours:
  //   * Station header → /feed?station=<code>.
  //   * Data cell with avg_dr_mm → /job/<latest_job_id>.
  //   * ✗ skip-compute-failed cell → /job/<last_skip_job_id>
  //     (representative DetailScreen — same pattern as ○).
  //   * ○ skip-no-reference cell → /job/<last_skip_job_id>.
  //   * Legacy dash (failed_count > 0, no skip wins) → /feed with
  //     station + config + failed_category trio so the operator
  //     lands on the exact FAILED rows.
  //   * Empty cell — no action; cursor stays default so «we
  //     haven't tried this» reads visually as a no-op.
  const goToStationFeed = (station: string) =>
    navigate(`/?station=${encodeURIComponent(station)}`);
  const goToJob = (jobId: number) => navigate(`/job/${jobId}`);
  const goToFailedFeed = (
    station: string,
    config: string,
    category: string,
  ) =>
    navigate(
      `/?station=${encodeURIComponent(station)}` +
        `&config=${encodeURIComponent(config)}` +
        `&failed_category=${encodeURIComponent(category)}`,
    );

  // Phase 6.6 — shared row renderer used both in the main filled
  // table and (when expanded) in the empty-stations section.
  // Keeps the three-state cell logic in one place so a future
  // tweak to the dash / empty / number rendering can't drift
  // between the two sections.
  const renderRow = (station: string) => (
    <tr key={station}>
      <th
        className="heatmap-row-header clickable"
        role="button"
        tabIndex={0}
        onClick={() => goToStationFeed(station)}
        onKeyDown={_onKeyboardActivate(() =>
          goToStationFeed(station),
        )}
        title={`Открыть Ленту с фильтром по ${station}`}
        data-testid={`heatmap-row-header-${station}`}
      >
        {station}
      </th>
      {data.configs.map((config) => {
        const cell = cellByKey.get(`${station}|${config}`);
        const dr = cell?.avg_dr_mm ?? null;
        const failedCount = cell?.failed_count ?? 0;
        const skipCompute = cell?.skip_compute_failed_count ?? 0;
        const skipNoRef = cell?.skip_no_reference_count ?? 0;
        // P1 · 2B — technical/indeterminate family (operational failure,
        // unknown outcome, invariant violation). Counted separately in the
        // data; rolled into ONE visible ⚡ glyph so a 401/timeout reads as a
        // technical problem, never «нет эталона» (○) and never an empty cell.
        const skipOperational = cell?.skip_operational_failure_count ?? 0;
        const skipUnknown = cell?.skip_unknown_count ?? 0;
        const skipInvariant = cell?.skip_invariant_violation_count ?? 0;
        const skipTechnical = skipOperational + skipUnknown + skipInvariant;
        // Phase G.2 — five-state precedence per spec §G.2:
        //   number > skip-(compute|no-ref) > legacy-dash > empty.
        // Number wins because a measured value is the most
        // informative signal. Among skip glyphs, the dominant
        // count wins; tiebreak on ✗ (compute_failed is the
        // earlier-discovered case, operator already has heuristics
        // for it). Legacy «—» (failed_count) only fires when no
        // skip is present — a job that ran and was skipped tells
        // us more than a job that never started.
        const cellState:
          | 'number'
          | 'skip-technical'
          | 'skip-compute'
          | 'skip-no-ref'
          | 'dash'
          | 'empty' =
          dr !== null
            ? 'number'
            : skipTechnical > 0
              ? 'skip-technical'
              : skipCompute > 0 && skipCompute >= skipNoRef
                ? 'skip-compute'
                : skipNoRef > 0
                  ? 'skip-no-ref'
                  : failedCount > 0
                    ? 'dash'
                    : 'empty';
        const isSkip =
          cellState === 'skip-technical' ||
          cellState === 'skip-compute' ||
          cellState === 'skip-no-ref';
        const clickable =
          (cellState === 'number' && cell?.latest_job_id != null) ||
          (isSkip && cell?.last_skip_job_id != null) ||
          (cellState === 'dash' &&
            cell?.last_failed_job_id != null &&
            cell?.last_failed_category != null);
        const onActivate: (() => void) | undefined =
          cellState === 'number' && cell?.latest_job_id != null
            ? () => goToJob(cell.latest_job_id!)
            : isSkip && cell?.last_skip_job_id != null
              ? () => goToJob(cell.last_skip_job_id!)
              : cellState === 'dash' && cell?.last_failed_category != null
                ? () =>
                    goToFailedFeed(
                      station,
                      config,
                      cell.last_failed_category as string,
                    )
                : undefined;
        const bg =
          cellState === 'number'
            ? drColor(dr)
            : cellState === 'empty'
              ? 'var(--surface)'
              : 'var(--surface-2)';
        const fg =
          cellState === 'number' ? drTextColor(dr) : 'var(--ink-3)';
        const tooltip =
          cellState === 'number'
            ? `${station} × ${formatConfig(
                config,
              )}: ${dr!.toFixed(1)} мм (n=${cell?.count ?? 0})`
            : cellState === 'skip-technical'
              ? `${station} × ${formatConfig(
                  config,
                )}: техническая ошибка получения эталона — ${skipOperational} операц. (напр. 401/timeout)${
                  skipUnknown > 0 ? `, ${skipUnknown} неизв.` : ''
                }${
                  skipInvariant > 0 ? `, ${skipInvariant} несоответствие данных` : ''
                }. Это НЕ «нет эталона».`
              : cellState === 'skip-compute'
                ? `${station} × ${formatConfig(
                    config,
                  )}: ${skipCompute} задач без решения (PPP не дал координат)`
                : cellState === 'skip-no-ref'
                  ? `${station} × ${formatConfig(
                      config,
                    )}: ${skipNoRef} задач без эталона (нет IGS Daily в ±5 дней)`
                  : cellState === 'dash'
                    ? `${station} × ${formatConfig(
                        config,
                      )}: ${failedCount} задач FAILED, последняя — ${
                        FAILED_CATEGORY_SHORT_LABEL[
                          cell!.last_failed_category as FailedCategory
                        ] ?? cell?.last_failed_category
                      }`
                    : `${station} × ${formatConfig(
                        config,
                      )}: не запускали`;
        const glyph =
          cellState === 'number'
            ? dr!.toFixed(1)
            : cellState === 'skip-technical'
              ? '⚡'
              : cellState === 'skip-compute'
                ? '✗'
                : cellState === 'skip-no-ref'
                  ? '○'
                  : cellState === 'dash'
                    ? '—'
                    : '';
        return (
          <td
            key={config}
            className={[
              'heatmap-cell',
              cellState === 'dash' ? 'heatmap-cell-dash' : '',
              cellState === 'skip-technical'
                ? 'heatmap-cell-skip-technical'
                : '',
              cellState === 'skip-compute' ? 'heatmap-cell-skip-compute' : '',
              cellState === 'skip-no-ref' ? 'heatmap-cell-skip-no-ref' : '',
              cellState === 'empty' ? 'heatmap-cell-empty' : '',
              clickable ? 'clickable' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ backgroundColor: bg, color: fg }}
            data-testid={`heatmap-cell-${station}-${config}`}
            data-cell-state={cellState}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? onActivate : undefined}
            onKeyDown={
              clickable && onActivate
                ? _onKeyboardActivate(onActivate)
                : undefined
            }
            title={tooltip}
          >
            {glyph}
          </td>
        );
      })}
    </tr>
  );

  return (
    <div className="heatmap-section">
      <div className="section-title">точность по станциям и конфигам (|dR|, мм)</div>

      {/* Phase G.2 — three-glyph legend, repeated above AND below
          the table per spec §G.2: таблица длинная, чтобы оператор
          не листал к подвалу для расшифровки. */}
      <HeatmapDashLegend position="top" />

      {/* Phase 6.6 E — sort selector. Lives above the table so
          the operator sees the sort context before scanning rows. */}
      <div className="heatmap-sort-row">
        <label
          className="heatmap-sort-label"
          htmlFor="heatmap-sort-select"
        >
          сортировка
        </label>
        <select
          id="heatmap-sort-select"
          className="filter-select gnss-qa-select"
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value as SortMode)}
          data-testid="heatmap-sort-select"
        >
          {(['name', 'coverage', 'accuracy'] as const).map((m) => (
            <option key={m} value={m}>
              {_SORT_LABELS[m]}
            </option>
          ))}
        </select>
      </div>

      <div className="heatmap-wrap">
        <table className="heatmap-table" data-testid="heatmap-table">
          <thead>
            <tr>
              <th className="heatmap-corner">станция</th>
              {data.configs.map((cfg) => (
                <th key={cfg} className="heatmap-col-header">
                  {formatConfig(cfg)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Phase 6.6 E — filled rows in sort-mode order. */}
            {sortedFilled.map((station) => renderRow(station))}
          </tbody>
        </table>
      </div>

      {/* Phase 6.6 F — empty-stations collapsible section. Hidden
          entirely when nothing's empty; auto-expanded when EVERY
          station is empty so the operator doesn't see a lone
          toggle with no other content. */}
      {empty.length > 0 && (
        <section
          className="empty-stations-section"
          data-testid="empty-stations-section"
        >
          <button
            type="button"
            className="empty-stations-toggle"
            onClick={() => setEmptyExpanded((v) => !v)}
            aria-expanded={effectiveEmptyExpanded}
            data-testid="empty-stations-toggle"
          >
            <span className="empty-stations-caret" aria-hidden="true">
              {effectiveEmptyExpanded ? '▴' : '▾'}
            </span>
            {' '}
            СТАНЦИИ БЕЗ РЕЗУЛЬТАТОВ ({empty.length})
          </button>
          {effectiveEmptyExpanded && (
            <div className="heatmap-wrap">
              <table
                className="heatmap-table empty-stations-table"
                data-testid="empty-stations-table"
              >
                <thead>
                  <tr>
                    <th className="heatmap-corner">станция</th>
                    {data.configs.map((cfg) => (
                      <th key={cfg} className="heatmap-col-header">
                        {formatConfig(cfg)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Empty section content is name-sorted regardless
                      of the main sort mode — alphabetical is the
                      least-surprising default and there's no
                      median/coverage signal to sort on anyway. */}
                  {[...empty]
                    .sort((a, b) => a.localeCompare(b))
                    .map((station) => renderRow(station))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <div className="heatmap-legend">
        <span className="legend-swatch" style={{ background: drColor(5) }}>
          &lt; 15
        </span>
        <span className="legend-swatch" style={{ background: drColor(25) }}>
          15–50
        </span>
        <span className="legend-swatch" style={{ background: drColor(75) }}>
          &gt; 50 мм
        </span>
      </div>

      {/* Phase G.2 — bottom legend mirror (operator who scrolled
          to the tail of a long table sees расшифровку без возврата). */}
      <HeatmapDashLegend position="bottom" />
    </div>
  );
}

/** C7a — single readonly source of truth for the heatmap legend: one entry per
 *  user-facing cell state, mirroring the `cellState` glyph switch above (число,
 *  ⚡ skip-technical, ✗ skip-compute, ○ skip-no-ref, — dash, пусто empty). Tests
 *  key off the machine `state`, never the Russian text. Rendered identically in
 *  both the top and bottom legend. C7a documents an EXISTING glyph — no colour,
 *  threshold or classification changes. */
export interface HeatmapLegendItem {
  state:
    | 'number'
    | 'skip-technical'
    | 'skip-compute'
    | 'skip-no-ref'
    | 'dash'
    | 'empty';
  glyph: string;
  text: string;
  ariaLabel?: string;
}
export const HEATMAP_LEGEND_ITEMS: readonly HeatmapLegendItem[] = [
  { state: 'number', glyph: 'число', text: 'точность |dR|, мм' },
  {
    state: 'skip-technical',
    glyph: '⚡',
    text: 'техническая ошибка получения эталона (напр. ошибка доступа или тайм-аут)',
    ariaLabel: 'техническая ошибка',
  },
  {
    state: 'skip-compute',
    glyph: '✗',
    text: 'сервис не вернул координат',
    ariaLabel: 'нет решения',
  },
  {
    state: 'skip-no-ref',
    glyph: '○',
    text: 'нет эталона в ±5 дней',
    ariaLabel: 'нет эталона',
  },
  { state: 'dash', glyph: '—', text: 'задача FAILED' },
  { state: 'empty', glyph: 'пусто', text: 'не тестировалось' },
];

/** Phase G.2 / C7a — расшифровка всех cell-глифов из `HEATMAP_LEGEND_ITEMS`.
 *  Рендерится сверху и снизу таблицы (`position` лишь меняет data-testid;
 *  визуально один и тот же блок). */
function HeatmapDashLegend({ position }: { position: 'top' | 'bottom' }) {
  return (
    <div
      className="heatmap-dash-legend"
      data-testid={`heatmap-dash-legend-${position}`}
    >
      {HEATMAP_LEGEND_ITEMS.map((item, i) => (
        <Fragment key={item.state}>
          {i > 0 && <span className="heatmap-dash-legend-sep">·</span>}
          <span
            className="heatmap-dash-legend-item"
            data-testid={`heatmap-legend-item-${item.state}`}
          >
            <span
              className="heatmap-dash-legend-glyph"
              {...(item.ariaLabel ? { 'aria-label': item.ariaLabel } : {})}
            >
              {item.glyph}
            </span>
            <span className="heatmap-dash-legend-text">{item.text}</span>
          </span>
        </Fragment>
      ))}
    </div>
  );
}

// C1.2 — memoized: only `data` is passed (stable via React Query
// structuralSharing), so a parent re-render from the status poll / light query
// no longer re-runs the station×config matrix.
export const HeatmapStationConfig = memo(HeatmapStationConfigImpl);
